import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DetailField } from '../DetailField';
import { PersonasListSection } from '../PersonasListSection';
import { GarantesListSection } from './GarantesListSection';
import { RetiroHistorialList } from '../retiros/RetiroHistorialList';
import { formatCurrency, formatDate } from '../../utils';
import otrosTramitesService from '../../services/otrosTramitesService';
import type { OtroTramiteRecord } from '../../types';

interface ViewOtroTramiteDialogProps {
  open: boolean;
  tramite: OtroTramiteRecord;
  onOpenChange: (open: boolean) => void;
}

export const ViewOtroTramiteDialog: React.FC<ViewOtroTramiteDialogProps> = ({
  open,
  tramite,
  onOpenChange
}) => {
  const tramiteDetailQuery = useQuery({
    queryKey: ['otros-tramites', 'detail-view', tramite.id],
    queryFn: () => otrosTramitesService.getById(tramite.id),
    enabled: open && Boolean(tramite.id),
    staleTime: 1000 * 30,
  });

  const effectiveTramite = tramiteDetailQuery.data ?? tramite;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle del trámite {effectiveTramite.id}</DialogTitle>
          <DialogDescription>Información general y financiera del trámite seleccionado.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="retiros">Retiros</TabsTrigger>
          </TabsList>

          {/* Tab General */}
          <TabsContent value="general" className="space-y-6 mt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Tipo" value={effectiveTramite.tipo} />
              <DetailField label="Detalle" value={effectiveTramite.detalle?.trim() ? effectiveTramite.detalle : '-'} />
              <DetailField label="Copias" value={`${effectiveTramite.copiasActuales}/${effectiveTramite.copiasTotales}`} />
              <DetailField label="Cantidad de otorgantes" value={`${effectiveTramite.otorgantesCount}`} />
              <DetailField label="Cantidad de responsables" value={`${effectiveTramite.responsablesCount}`} />
              <DetailField label="Cantidad de garantes" value={`${effectiveTramite.garantesCount ?? 0}`} />
            </div>

            <section className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Monto (ARS)" value={formatCurrency(effectiveTramite.montoARS, 'ARS')} />
              <DetailField label="Monto (USD)" value={formatCurrency(effectiveTramite.montoUSD, 'USD')} />
              <DetailField label="Cobro (ARS)" value={effectiveTramite.cobroARS} />
              <DetailField label="Cobro (USD)" value={effectiveTramite.cobroUSD} />
              <DetailField label="Fecha" value={formatDate(effectiveTramite.fecha)} />
            </section>

            <PersonasListSection
              title="Otorgantes asociados"
              personas={effectiveTramite.otorgantes}
              personaType="otorgante"
              dniDocuments={effectiveTramite.dniDocuments}
            />

            <PersonasListSection
              title="Responsables asociados"
              personas={effectiveTramite.responsables}
              personaType="responsable"
              dniDocuments={effectiveTramite.dniDocuments}
            />

            {(effectiveTramite.garantesCount ?? 0) > 0 && (
              <GarantesListSection garantes={effectiveTramite.garantes ?? []} />
            )}
          </TabsContent>

          {/* Tab Retiros */}
          <TabsContent value="retiros" className="mt-4">
            <RetiroHistorialList
              otroTramiteId={effectiveTramite.id}
              copiasDisponibles={effectiveTramite.copiasTotales - effectiveTramite.copiasActuales}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
